import * as fabric from 'office-ui-fabric-react';
import { FC, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../../../../api/generated';
import { KendoLoading } from '../../../../components';
import { useConfirmation } from '../../../../components/dialogs';
import { LocationContext } from '../../../../components/LocationContext';
import { useWindow } from '../../../../components/windows/WindowContext';
import * as enums from '../../../../enums';
import { deviceType, DeviceType, infoScreenDisplayType, InfoScreenDisplayType, locationType } from '../../../../enums';
import { ExternalServiceType } from '../../../../enums/externalServiceType';
import { IDevice } from '../../../../models';
import LocationConfigDoorScreenLocationsWindow from './locationConfig/doorScreen/LocationConfigDoorScreenLocationsWindow';
import LocationConfigInfoScreenLocationsWindow from './locationConfig/infoScreen/LocationConfigInfoScreenLocationsWindow';
import LocationConfigInfoScreenScheduleWindow from './locationConfig/infoScreen/LocationConfigInfoScreenScheduleWindow';
import LocationConfigTerminalWindow from './terminal/LocationConfigTerminalWindow';

const LocationsPageDevices: FC = () => {
    const { organizationInformationId } = useParams<'organizationInformationId'>();

    const { state, getDevices } = useContext(LocationContext);
    const [isLoading, setIsLoading] = useState(false);
    const [externalService, setExternalService] = useState<ExternalServiceType>(ExternalServiceType.Nugis);
    const { confirmAsync } = useConfirmation();
    const { openWindow } = useWindow();

    useEffect(() => {
        const fetchData = async () => {
            if (state.selectedLocation !== null) {
                const externalServiceResponse: ExternalServiceType = await (await fetch('/api/config/external-service/type')).json();
                setExternalService(externalServiceResponse);
                await getDevices(parseInt(organizationInformationId.toString()), state.selectedLocation.id);
            }
        };

        fetchData();
    }, [state.selectedLocation, state.selectedDeviceId]);

    const handleDeviceAssign = async (item: IDevice) => {
        try {
            setIsLoading(true);
            if (state.selectedLocation !== null) {
                await api.administration.updateDeviceLocationId({
                    id: item.id,
                    deviceType: item.deviceType,
                    locationId: state.selectedLocation.id
                });
            }

            toast.success('Įrenginys sėkmingai priskirtas prie lokacijos');
            await getDevices(parseInt(organizationInformationId.toString()), state.selectedLocation!.id);
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
        }
    };

    const GetDeviceIcon = (item: IDevice) => {
        switch (item.deviceType) {
            case deviceType.InfoScreen:
                if (item.infoScreenDisplayType === infoScreenDisplayType.Schedule) {
                    return 'Calendar';
                } else {
                    return 'Location';
                }

            default:
                return 'Settings';
        }
    };

    const handleDeviceUnassignAprove = async (item: IDevice) => {
        try {
            const { success } = await confirmAsync(
                'Įrenginio atskyrimas nuo lokacijos',
                'Ar tikrai norite atskirti įrenginį nuo redaguojamos lokacijos?'
            );

            if (!success) {
                return;
            }

            setIsLoading(true);
            await api.administration.updateDeviceLocationId({
                id: item.id,
                locationId: null,
                deviceType: item.deviceType
            });

            toast.error('Įrenginys sėkmingai atskirtas nuo lokacijos');
            await getDevices(parseInt(organizationInformationId.toString()), state.selectedLocation!.id);
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
        }
    };

    const openDeviceEditWindow = async (
        editableDeviceId: number,
        deviceType: DeviceType,
        infoScreenDisplayType: InfoScreenDisplayType = enums.infoScreenDisplayType.Locations
    ) => {
        try {
            switch (deviceType) {
                case enums.deviceType.DoorScreen:
                    LocationConfigDoorScreenLocationsWindow.defaultProps = {
                        componentProps: {
                            doorScreenId: editableDeviceId
                        }
                    };
                    openWindow({
                        title: 'Durų ekrano konfigūracija',
                        component: LocationConfigDoorScreenLocationsWindow,
                        isDialogBehaviour: true,
                        width: 1280,
                        height: 720
                    });
                    break;
                case enums.deviceType.Terminal:
                    LocationConfigTerminalWindow.defaultProps = {
                        componentProps: {
                            terminalId: editableDeviceId,
                            organizationInformationId: parseInt(organizationInformationId)
                        }
                    };
                    openWindow({
                        title: 'Terminalo konfigūracija',
                        component: LocationConfigTerminalWindow,
                        isDialogBehaviour: true,
                        width: 1280,
                        height: 720
                    });
                    break;
                case enums.deviceType.InfoScreen:
                    switch (infoScreenDisplayType) {
                        case enums.infoScreenDisplayType.Schedule:
                            LocationConfigInfoScreenScheduleWindow.defaultProps = {
                                componentProps: {
                                    infoScreenId: editableDeviceId
                                }
                            };
                            openWindow({
                                title: 'Info ekrano konfigūracija',
                                component: LocationConfigInfoScreenScheduleWindow,
                                isDialogBehaviour: true,
                                width: 1280,
                                height: 720
                            });
                            break;
                        case enums.infoScreenDisplayType.Locations:
                            LocationConfigInfoScreenLocationsWindow.defaultProps = {
                                componentProps: {
                                    infoScreenId: editableDeviceId
                                }
                            };
                            openWindow({
                                title: 'Info ekrano konfigūracija',
                                component: LocationConfigInfoScreenLocationsWindow,
                                isDialogBehaviour: true,
                                width: 1280,
                                height: 720
                            });
                            break;
                    }
            }
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const _devicesColumns = [
        {
            key: 'column1',
            name: 'ID',
            fieldName: 'id',
            minWidth: 50,
            maxWidth: 50,
            isResizable: true
        },
        {
            key: 'column2',
            name: 'Pavadinimas',
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true
        },
        {
            key: 'column3',
            name: 'Tipas',
            fieldName: 'type',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: IDevice) => deviceType.format(item.deviceType)
        },
        {
            key: 'column4',
            name: 'Modelis',
            fieldName: 'model',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true
        },
        {
            key: 'column5',
            name: 'Veiksmai',
            fieldName: '',
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,

            onRender: (item: IDevice) => {
                if (item.locationId === null) {
                    return (
                        <fabric.IconButton
                            disabled={state.selectedLocation?.locationType !== locationType.Level && item.deviceType !== deviceType.DoorScreen}
                            onClick={async () => await handleDeviceAssign(item)}
                            iconProps={{ iconName: 'Add', styles: { root: { color: fabric.getTheme().semanticColors.successText } } }}
                        />
                    );
                } else {
                    return (
                        <>
                            {item.deviceType === deviceType.InfoScreen &&
                                item.infoScreenDisplayType === infoScreenDisplayType.Schedule &&
                                externalService === ExternalServiceType.Varis && (
                                    <div style={{ display: 'inline-block' }}>
                                        <fabric.IconButton
                                            onClick={() => {
                                                openDeviceEditWindow(item.id, item.deviceType, infoScreenDisplayType.Locations);
                                            }}
                                            iconProps={{
                                                iconName: 'Location'
                                            }}
                                        />
                                    </div>
                                )}

                            <div style={{ display: 'inline-block' }}>
                                <fabric.IconButton
                                    onClick={() => {
                                        openDeviceEditWindow(item.id, item.deviceType, item.infoScreenDisplayType);
                                    }}
                                    iconProps={{
                                        iconName: GetDeviceIcon(item)
                                    }}
                                />
                            </div>
                            <div style={{ display: 'inline-block', marginLeft: '10px' }}>
                                <fabric.IconButton
                                    onClick={async () => await handleDeviceUnassignAprove(item)}
                                    iconProps={{
                                        iconName: 'Cancel',
                                        styles: { root: { color: fabric.getTheme().semanticColors.errorText } }
                                    }}
                                />
                            </div>
                        </>
                    );
                }
            }
        }
    ];

    return (
        <>
            {state.selectedLocation && (
                <div style={{ marginTop: '30px', marginBottom: '20px' }}>
                    <hr />
                    <div className="ms-Grid-row">
                        <fabric.Text style={{ fontWeight: 'lighter' }} variant="xLargePlus">
                            Nepriskirti įrenginiai
                        </fabric.Text>
                        {state.unassignedDevices ? (
                            <fabric.DetailsList columns={_devicesColumns} items={state.unassignedDevices} selectionMode={fabric.SelectionMode.none} />
                        ) : (
                            <KendoLoading />
                        )}
                    </div>
                    <div className="ms-Grid-row">
                        <fabric.Text style={{ fontWeight: 'lighter' }} variant="xLargePlus">
                            Priskirti įrenginiai
                        </fabric.Text>
                        {state.assignedDevices ? (
                            <fabric.DetailsList selectionMode={fabric.SelectionMode.none} columns={_devicesColumns} items={state.assignedDevices} />
                        ) : (
                            <KendoLoading />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default LocationsPageDevices;
